import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, Sound } from '~/assets/sounds';
import { useZoomFactor } from '~/hooks';
import { rage } from '~/rage';
import { useShake } from '../../hooks';
import { AppConnectWiresHelpButton } from '../AppConnectWiresHelpButton';
import { AppConnectWiresStatus } from '../AppConnectWiresStatus';
import { AppConnectWiresWire } from '../AppConnectWiresWire';
import { ErrorOverlay } from '../ErrorOverlay';
import { generateGameField } from './AppConnectWiresGameField.model';
import {
	StyledContainer,
	StyledWireContainer,
	StyledWireLine,
	StyledWiresContainer,
} from './AppConnectWiresGameField.styled';

interface WireConnection {
	from: number;
	to: number;
	color: {
		start: string;
		end: string;
	};
}

const AppConnectWiresGameField = ({
	handleGameSolved,
}: {
	handleGameSolved: () => void;
}) => {
	const { leftColors, rightColors, positions } = useMemo(
		() => generateGameField(),
		[],
	);
	const [connections, setConnections] = useState<WireConnection[]>([]);
	const [draggingWire, setDraggingWire] = useState<{
		index: number;
		color: { start: string; end: string };
		startPosition: { x: number; y: number };
		side: 'left' | 'right';
	} | null>(null);
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();
	const [gameSolvedNotificationShown, setGameSolvedNotificationShown] =
		useState(false);
	const [errorIndex, setErrorIndex] = useState<{
		from: { index: number; side: 'left' | 'right' };
		to: { index: number; side: 'left' | 'right' };
	} | null>(null);
	const [errorCount, setErrorCount] = useState(0);
	const [showErrorScreen, setShowErrorScreen] = useState(false);
	const { shake } = useShake();

	const isWireConnected = (index: number, side: 'left' | 'right') => {
		return connections.some(
			(conn) =>
				(side === 'left' && conn.from === index) ||
				(side === 'right' && conn.to === index),
		);
	};

	const connectionsStatus = useMemo(
		() =>
			leftColors.map((leftColor, index) => {
				const connection = connections.find((conn) => conn.from === index);
				if (!connection) return false;

				const rightColor = rightColors[connection.to];
				return (
					leftColor.start === rightColor.start &&
					leftColor.end === rightColor.end
				);
			}),
		[connections, leftColors, rightColors],
	);

	const getHelpButtonType = (index: number, side: 'left' | 'right') => {
		if (
			errorIndex &&
			((errorIndex.from.index === index && errorIndex.from.side === side) ||
				(errorIndex.to.index === index && errorIndex.to.side === side))
		) {
			return 'error';
		}

		if (isWireConnected(index, side)) {
			return 'help';
		}

		if (!draggingWire) return 'normal';
		if (draggingWire.side === side) return 'normal';
		return 'help';
	};

	const shouldShowHelpButton = (index: number, side: 'left' | 'right') => {
		if (isWireConnected(index, side)) {
			return true;
		}

		if (!draggingWire) return true;
		if (draggingWire.side === side) {
			return draggingWire.index === index;
		}
		return true;
	};

	const handleMouseDown = (
		index: number,
		color: { start: string; end: string },
		side: 'left' | 'right',
		e: React.MouseEvent<HTMLDivElement>,
	) => {
		if (isWireConnected(index, side)) {
			return;
		}

		if (draggingWire !== null) {
			handleMouseUp(index, side);
			return;
		}

		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			const startPosition = {
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			};
			setDraggingWire({ index, color, startPosition, side });
			setMousePosition(startPosition);
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (draggingWire !== null && containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			setMousePosition({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			});
		}
	};

	const showError = () => {
		setShowErrorScreen(true);
		shake();
		setTimeout(() => {
			setShowErrorScreen(false);
		}, 3000);
	};

	const handleMouseUp = (index: number, side: 'left' | 'right') => {
		if (draggingWire !== null) {
			if (draggingWire.index === index && draggingWire.side === side) {
				return;
			}

			const targetColor =
				side === 'left' ? leftColors[index] : rightColors[index];
			const sourceColor = draggingWire.color;

			if (
				targetColor.start === sourceColor.start &&
				targetColor.end === sourceColor.end &&
				draggingWire.side !== side
			) {
				const newConnection = {
					from: draggingWire.side === 'left' ? draggingWire.index : index,
					to: draggingWire.side === 'left' ? index : draggingWire.index,
					color: draggingWire.color,
				};
				setConnections((prev) => [...prev, newConnection]);
			} else {
				setErrorIndex({
					from: { index: draggingWire.index, side: draggingWire.side },
					to: { index, side },
				});
				playSound(Sound.ElectricLoss);
				setTimeout(() => {
					setErrorIndex(null);
				}, 3000);

				const newErrorCount = errorCount + 1;
				setErrorCount(newErrorCount);
				if (newErrorCount >= 2) {
					showError();
				}
			}
			setDraggingWire(null);
		}
	};

	const handleMouseLeave = () => {
		setDraggingWire(null);
	};

	useEffect(() => {
		if (
			connectionsStatus.every((status) => status) &&
			!gameSolvedNotificationShown
		) {
			setGameSolvedNotificationShown(true);
			handleGameSolved();
			setTimeout(() => {
				rage.notifications.showSuccess('Вы починили проводку');
			}, 1000);
		}
	}, [connectionsStatus, handleGameSolved, gameSolvedNotificationShown]);

	return (
		<StyledContainer
			ref={containerRef}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
		>
			<AppConnectWiresStatus status={connectionsStatus} />
			<StyledWiresContainer style={{ top: positions.top, gap: positions.gap }}>
				{leftColors.map((color, index) => (
					<StyledWireContainer key={index}>
						<AppConnectWiresWire colors={color} side="left" />
						<AppConnectWiresHelpButton
							type={getHelpButtonType(index, 'left')}
							active={isWireConnected(index, 'left')}
							onMouseDown={(e) => handleMouseDown(index, color, 'left', e)}
							onMouseUp={() => handleMouseUp(index, 'left')}
							style={{
								opacity: shouldShowHelpButton(index, 'left') ? 1 : 0,
							}}
						/>
					</StyledWireContainer>
				))}
			</StyledWiresContainer>
			<StyledWiresContainer
				style={{ top: positions.top, gap: positions.gap, right: 0 }}
			>
				{rightColors.map((color, index) => (
					<StyledWireContainer key={index}>
						<AppConnectWiresHelpButton
							type={getHelpButtonType(index, 'right')}
							active={isWireConnected(index, 'right')}
							onMouseDown={(e) => handleMouseDown(index, color, 'right', e)}
							onMouseUp={() => handleMouseUp(index, 'right')}
							style={{
								opacity: shouldShowHelpButton(index, 'right') ? 1 : 0,
							}}
						/>
						<AppConnectWiresWire colors={color} side="right" />
					</StyledWireContainer>
				))}
			</StyledWiresContainer>
			{connections.map((connection, index) => (
				<StyledWireLine
					key={index}
					$from={connection.from}
					$to={connection.to}
					$positions={positions}
					$color={connection.color}
					$zoomFactor={zoomFactor}
				/>
			))}
			{draggingWire !== null && (
				<StyledWireLine
					$from={draggingWire.index}
					$to={-1}
					$positions={positions}
					$mousePosition={mousePosition}
					$color={draggingWire.color}
					$zoomFactor={zoomFactor}
					$draggingSide={draggingWire.side}
				/>
			)}
			<CSSTransition
				in={showErrorScreen}
				classNames="overlay"
				timeout={300}
				unmountOnExit
			>
				<ErrorOverlay />
			</CSSTransition>
		</StyledContainer>
	);
};

export { AppConnectWiresGameField };
